import { videosPartA } from "../mocks/videosPartA";
import { videosPartB } from "../mocks/videosPartB";
import { categories, regions, years } from "../mocks/categories";

export interface Video {
  id: string;
  title: string;
  categoryId: string;
  cover: string;
  duration: string;
  uploadedAt: string;
  year: string;
  region: string;
  type: string;
  playCount: number;
  rating: number;
  uploader: string;
  description: string;
  tags: string[];
}

export interface Category {
  id: string;
  name: string;
  icon: string;
  description: string;
}

export const allVideos: Video[] = [...videosPartA, ...videosPartB] as Video[];

export const allCategories: Category[] = categories as Category[];

export { regions, years };

export const getCategoryById = (id: string): Category | undefined =>
  allCategories.find((c) => c.id === id);

export const getCategoryName = (id: string): string =>
  getCategoryById(id)?.name ?? "未分类";

export const getVideoById = (id: string): Video | undefined =>
  allVideos.find((v) => v.id === id);

export const getVideosByCategory = (categoryId: string): Video[] =>
  allVideos.filter((v) => v.categoryId === categoryId);

export const getFeaturedVideos = (): Video[] =>
  [...allVideos].sort((a, b) => b.playCount - a.playCount).slice(0, 5);

export const getLatestVideos = (count = 12): Video[] =>
  [...allVideos]
    .sort((a, b) => (a.uploadedAt < b.uploadedAt ? 1 : -1))
    .slice(0, count);

export const getHotVideos = (count = 8): Video[] =>
  [...allVideos].sort((a, b) => b.playCount - a.playCount).slice(0, count);

export const getRelatedVideos = (video: Video, count = 5): Video[] => {
  const sameCategory = allVideos.filter(
    (v) => v.id !== video.id && v.categoryId === video.categoryId,
  );
  const sameTag = allVideos.filter(
    (v) =>
      v.id !== video.id &&
      v.categoryId !== video.categoryId &&
      v.tags.some((t) => video.tags.includes(t)),
  );
  return [...sameCategory, ...sameTag].slice(0, count);
};

export const searchVideos = (keyword: string): Video[] => {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return allVideos.filter((v) => {
    const haystack = [
      v.title,
      v.type,
      v.region,
      v.uploader,
      v.description,
      ...v.tags,
      getCategoryName(v.categoryId),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw);
  });
};

export interface FilterState {
  year: string;
  region: string;
  type: string;
}

export const getVideoTypes = (categoryId?: string): string[] => {
  const source = categoryId
    ? allVideos.filter((v) => v.categoryId === categoryId)
    : allVideos;
  const set = new Set(source.map((v) => v.type));
  return Array.from(set);
};

export const applyFilters = (
  videos: Video[],
  filter: FilterState,
): Video[] =>
  videos.filter((v) => {
    if (filter.year !== "全部" && v.year !== filter.year) return false;
    if (filter.region !== "全部" && v.region !== filter.region) return false;
    if (filter.type !== "全部" && v.type !== filter.type) return false;
    return true;
  });

export const applySort = (
  videos: Video[],
  sort: string,
): Video[] => {
  const list = [...videos];
  if (sort === "newest") {
    return list.sort((a, b) => (a.uploadedAt < b.uploadedAt ? 1 : -1));
  }
  if (sort === "hot") {
    return list.sort((a, b) => b.playCount - a.playCount);
  }
  return list;
};

export const formatPlayCount = (count: number): string => {
  if (count >= 10000) {
    return `${(count / 10000).toFixed(1)}万`;
  }
  return `${count}`;
};

export const getCategoryCount = (categoryId: string): number =>
  allVideos.filter((v) => v.categoryId === categoryId).length;